import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Save, X, Edit2, Trash2, Archive, FileEdit, Send, Filter } from "lucide-react";
import { toast } from "sonner";

const DOMAINS = ["BHV", "FND", "O2O", "INF", "REC", "INV", "CRO", "PRE", "TEM"];
const FORMATS = ["C", "L", "D"];
const FAMILIES = ["TRANSVERSAL", "TISSAGE", "BUSINESS", "SOURCING", "RAYONNEMENT"];
const NIVEAU_ANIMATEUR = ["Membre", "Consultant", "DR", "DN"];
const PHASES = ["Création", "Démarrage", "Croisière", "Mature", "Revitalisation"];
const EFFETS = ["Réflexion", "Activation", "Décision", "Compétence"];

const EMPTY = {
  id: "",
  domain: "BHV",
  format: "C",
  family: "TRANSVERSAL",
  title_fr: "",
  title_en: "",
  sub_domain: "",
  description: "",
  niveau_animateur: "DR",
  niveau_membre: "Débutant",
  effet_attendu: "Réflexion",
  duree_minutes: 60,
  symptomes_traites: [],
  kpis_cibles: [],
  profil_bpm_cible: [],
  phase_groupe: [],
  prerequis: [],
  gdrive_ppt_fr: "",
  gdrive_script_fr: "",
  gdrive_ppt_en: "",
  gdrive_script_en: "",
  gdrive_folder: "",
  status: "draft",
  external_links: {},
};

export default function AtelierEditorPage() {
  const { t, i18n } = useTranslation();
  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const isEn = uiLocale === "en";
  const [ateliers, setAteliers] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("metadata");
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyRow, setBusyRow] = useState(null);

  const loadAll = async () => {
    const [a, s, k] = await Promise.all([
      api.get("/ateliers", { params: { status_filter: "" } }),
      api.get("/reference/symptoms"),
      api.get("/reference/kpis"),
    ]);
    setAteliers(a.data);
    setSymptoms(s.data);
    setKpis(k.data);
  };

  useEffect(() => { loadAll(); }, []);

  const changeStatus = async (atelier, newStatus) => {
    if (atelier.status === newStatus) return;
    const labels_fr = { draft: "brouillon", published: "publié", archived: "archivé" };
    const labels_en = { draft: "draft", published: "published", archived: "archived" };
    const labels = isEn ? labels_en : labels_fr;
    const confirmMsg = isEn
      ? `Switch "${atelier.title_fr}" to ${labels[newStatus]}?`
      : `Passer « ${atelier.title_fr} » en ${labels[newStatus]} ?`;
    if (!window.confirm(confirmMsg)) return;
    setBusyRow(atelier.id);
    try {
      await api.patch(`/ateliers/${atelier.id}/status`, { status: newStatus });
      toast.success(`${isEn ? "Workshop" : "Atelier"} ${atelier.id} → ${labels[newStatus]}`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("catalogue.action_impossible"));
    } finally {
      setBusyRow(null);
    }
  };

  const hardDelete = async (atelier) => {
    setBusyRow(atelier.id);
    try {
      const { data: usage } = await api.get(`/ateliers/${atelier.id}/usage`);
      if (!usage.can_delete) {
        toast.error(
          isEn
            ? `Deletion blocked: ${usage.prescribed_count} prescription(s) and ${usage.realised_count} realization(s) in history. Archive instead.`
            : `Suppression impossible : ${usage.prescribed_count} prescription(s) et ${usage.realised_count} réalisation(s) en historique. Archivez plutôt.`,
        );
        return;
      }
      const msg = isEn
        ? `⚠ Permanent deletion of "${atelier.title_fr}" (${atelier.id}).\n\nThis workshop has never been prescribed nor realized.\nContinue?`
        : `⚠ Suppression DÉFINITIVE de « ${atelier.title_fr} » (${atelier.id}).\n\nCet atelier n'a jamais été prescrit ni réalisé.\nContinuer ?`;
      if (!window.confirm(msg)) return;
      await api.delete(`/ateliers/${atelier.id}`);
      toast.success(`${isEn ? "Workshop" : "Atelier"} ${atelier.id} ${isEn ? "deleted" : "supprimé"}`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEn ? "Deletion not possible" : "Suppression impossible"));
    } finally {
      setBusyRow(null);
    }
  };

  const startNew = () => {
    setEditing({ ...EMPTY });
    setTab("metadata");
  };

  const startEdit = (a) => {
    setEditing({ ...EMPTY, ...a });
    setTab("metadata");
  };

  const save = async (publish = false) => {
    if (!editing.id || !editing.title_fr) {
      toast.error(isEn ? "ID and FR title required" : "ID et titre FR requis");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (publish) payload.status = "published";
      const exists = ateliers.find((a) => a.id === editing.id);
      if (exists) {
        await api.put(`/ateliers/${editing.id}`, payload);
        toast.success(`${isEn ? "Workshop" : "Atelier"} ${editing.id} ${isEn ? "updated" : "mis à jour"}`);
      } else {
        await api.post("/ateliers", payload);
        toast.success(`${isEn ? "Workshop" : "Atelier"} ${editing.id} ${isEn ? "created" : "créé"}`);
      }
      setEditing(null);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEn ? "Error" : "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  const toggleArr = (key, val) => {
    setEditing((prev) => {
      const arr = prev[key] || [];
      return arr.includes(val) ? { ...prev, [key]: arr.filter((v) => v !== val) } : { ...prev, [key]: [...arr, val] };
    });
  };

  if (editing) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="atelier-editor-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold">{isEn ? "Editor" : "Éditeur"}</div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">{editing.id ? `${isEn ? "Workshop" : "Atelier"} ${editing.id}` : t("editor.new_workshop")}</h1>
          </div>
          <Button variant="ghost" onClick={() => setEditing(null)} data-testid="btn-close-editor"><X className="w-4 h-4 mr-1" /> {t("editor.cancel")}</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="editor-tabs">
            <TabsTrigger value="metadata" data-testid="tab-metadata">{isEn ? "Metadata" : "Métadonnées"}</TabsTrigger>
            <TabsTrigger value="tagging" data-testid="tab-tagging">{isEn ? "AI Tagging" : "Tagging IA"}</TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">{isEn ? "Materials" : "Supports"}</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">{isEn ? "Preview" : "Aperçu"}</TabsTrigger>
          </TabsList>

          <TabsContent value="metadata" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={isEn ? "Workshop ID" : "ID atelier"} required>
              <Input value={editing.id} onChange={(e) => setEditing({ ...editing, id: e.target.value.toUpperCase() })} placeholder="BHV-009-L" className="font-mono" data-testid="field-id" />
            </Field>
            <Field label={t("editor.status")}>
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="field-status">
                <option value="draft">{t("editor.draft")}</option>
                <option value="published">{t("editor.published")}</option>
                <option value="archived">{t("editor.archived")}</option>
              </select>
            </Field>
          </div>
          <Field label={t("editor.title_fr_label")} required>
            <Input value={editing.title_fr} onChange={(e) => setEditing({ ...editing, title_fr: e.target.value })} data-testid="field-title-fr" />
          </Field>
          <Field label={t("editor.title_en_label")}>
            <Input value={editing.title_en || ""} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="field-title-en" />
          </Field>
          <Field label={t("editor.description_label")}>
            <Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} data-testid="field-description" />
          </Field>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label={t("editor.domain_label")}>
              <select value={editing.domain} onChange={(e) => setEditing({ ...editing, domain: e.target.value })} className="w-full h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="field-domain">
                {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label={t("editor.format_label")}>
              <select value={editing.format} onChange={(e) => setEditing({ ...editing, format: e.target.value })} className="w-full h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="field-format">
                {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label={isEn ? "Family" : "Famille"}>
              <select value={editing.family} onChange={(e) => setEditing({ ...editing, family: e.target.value })} className="w-full h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="field-family">
                {FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label={t("editor.duration_label")}>
              <Input type="number" value={editing.duree_minutes} onChange={(e) => setEditing({ ...editing, duree_minutes: parseInt(e.target.value || "0", 10) })} data-testid="field-duree" />
            </Field>
            <Field label={t("editor.level_label")}>
              <select value={editing.niveau_animateur} onChange={(e) => setEditing({ ...editing, niveau_animateur: e.target.value })} className="w-full h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="field-niveau-animateur">
                {NIVEAU_ANIMATEUR.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <Field label={isEn ? "Expected effect" : "Effet attendu"}>
              <select value={editing.effet_attendu} onChange={(e) => setEditing({ ...editing, effet_attendu: e.target.value })} className="w-full h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="field-effet">
                {EFFETS.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </Field>
          </div>
          </TabsContent>

          <TabsContent value="tagging" className="space-y-4 pt-4">
            <MultiSelect label={t("atelier.symptoms_treated")} items={symptoms.map((s) => ({ value: s.code, label: s.label_fr }))} selected={editing.symptomes_traites} onToggle={(v) => toggleArr("symptomes_traites", v)} testIdPrefix="symptom" />
            <MultiSelect label={isEn ? "Target KPIs" : "KPI ciblés"} items={kpis.map((k) => ({ value: k, label: k }))} selected={editing.kpis_cibles} onToggle={(v) => toggleArr("kpis_cibles", v)} testIdPrefix="kpi" />
            <MultiSelect label={isEn ? "Target BPM profile" : "Profil BPM cible"} items={[{ value: "D", label: "D" }, { value: "I", label: "I" }, { value: "S", label: "S" }, { value: "C", label: "C" }, { value: "all", label: isEn ? "All" : "Tous" }]} selected={editing.profil_bpm_cible} onToggle={(v) => toggleArr("profil_bpm_cible", v)} testIdPrefix="bpm" />
            <MultiSelect label={isEn ? "Group phase" : "Phase du groupe"} items={PHASES.map((p) => ({ value: p, label: p }))} selected={editing.phase_groupe} onToggle={(v) => toggleArr("phase_groupe", v)} testIdPrefix="phase" />
            <MultiSelect label={isEn ? "Prerequisites (workshop IDs)" : "Prérequis (atelier IDs)"} items={ateliers.filter((a) => a.id !== editing.id).map((a) => ({ value: a.id, label: `${a.id} · ${a.title_fr}` }))} selected={editing.prerequis} onToggle={(v) => toggleArr("prerequis", v)} testIdPrefix="prereq" />
          </TabsContent>

          <TabsContent value="files" className="space-y-3 pt-4">
            <div className="bg-apollo-orange-light/40 border border-apollo-orange/20 rounded-lg p-3 text-sm text-apollo-orange-dark" data-testid="files-help-text">
              {isEn ? (
                <><strong>Google Drive links</strong> (APOLLO shared Drive). Paste the share URL or the file ID : the backend retrieves it via a read-only service account. No Google account needed on the user side.</>
              ) : (
                <><strong>Liens Google Drive</strong> (Drive partagé APOLLO). Collez l'URL de partage ou l'ID du fichier : le backend récupère via un compte de service en lecture seule. Aucun compte Google côté utilisateur.</>
              )}
            </div>
            <Field label={isEn ? "Drive link : PPT FR" : "Lien Drive : PPT FR"} required>
              <Input value={editing.gdrive_ppt_fr || ""} onChange={(e) => setEditing({ ...editing, gdrive_ppt_fr: e.target.value })} placeholder="https://drive.google.com/file/d/…/view" data-testid="field-gdrive-ppt-fr" />
            </Field>
            <Field label={isEn ? "Drive link : Facilitator script FR (.docx)" : "Lien Drive : Script animateur FR (.docx)"} required>
              <Input value={editing.gdrive_script_fr || ""} onChange={(e) => setEditing({ ...editing, gdrive_script_fr: e.target.value })} placeholder="https://drive.google.com/file/d/…/view" data-testid="field-gdrive-script-fr" />
            </Field>
            <Field label={isEn ? "Drive link : PPT EN (optional)" : "Lien Drive : PPT EN (optionnel)"}>
              <Input value={editing.gdrive_ppt_en || ""} onChange={(e) => setEditing({ ...editing, gdrive_ppt_en: e.target.value })} placeholder="https://drive.google.com/file/d/…/view" data-testid="field-gdrive-ppt-en" />
            </Field>
            <Field label={isEn ? "Drive link : Facilitator script EN (optional)" : "Lien Drive : Script animateur EN (optionnel)"}>
              <Input value={editing.gdrive_script_en || ""} onChange={(e) => setEditing({ ...editing, gdrive_script_en: e.target.value })} placeholder="https://drive.google.com/file/d/…/view" data-testid="field-gdrive-script-en" />
            </Field>
            <Field label={isEn ? "Drive link : Workshop folder (optional)" : "Lien Drive : Dossier de l'atelier (optionnel)"}>
              <Input value={editing.gdrive_folder || ""} onChange={(e) => setEditing({ ...editing, gdrive_folder: e.target.value })} placeholder="https://drive.google.com/drive/folders/…" data-testid="field-gdrive-folder" />
            </Field>
          </TabsContent>

          <TabsContent value="preview" className="pt-4">
            <div className="bg-bg-soft rounded-xl p-6">
              <h3 className="font-heading font-black text-2xl tracking-tight">{(isEn ? (editing.title_en || editing.title_fr) : editing.title_fr) || (isEn ? "Untitled" : "Sans titre")}</h3>
              <div className="text-sm text-text-secondary mt-1">{editing.id} · {editing.domain} · {editing.format} · {editing.duree_minutes}min</div>
              <p className="text-sm mt-3">{editing.description}</p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
          <Button onClick={() => save(false)} disabled={saving} variant="outline" data-testid="btn-save-draft">
            <Save className="w-4 h-4 mr-2" /> {isEn ? "Save as draft" : "Enregistrer en brouillon"}
          </Button>
          <Button onClick={() => save(true)} disabled={saving} className="bg-apollo-orange hover:bg-apollo-orange-dark text-white" data-testid="btn-publish">
            {saving ? (isEn ? "Publishing…" : "Publication…") : t("editor.publish")}
          </Button>
        </div>
      </div>
    );
  }

  const filtered = statusFilter === "all" ? ateliers : ateliers.filter((a) => a.status === statusFilter);
  const counts = ateliers.reduce(
    (acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }),
    { draft: 0, published: 0, archived: 0 },
  );

  return (
    <div className="space-y-6" data-testid="atelier-editor-list">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">{isEn ? "CEO Editor" : "Éditeur CEO"}</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">{isEn ? "Workshop catalog" : "Catalogue d'ateliers"}</h1>
          <p className="font-oracle italic text-lg text-text-secondary mt-2">« {ateliers.length} {isEn ? "workshops in memory." : "ateliers en mémoire."} »</p>
        </div>
        <Button onClick={startNew} className="bg-apollo-orange hover:bg-apollo-orange-dark text-white" data-testid="btn-new-atelier">
          <Plus className="w-4 h-4 mr-2" /> {t("editor.new_workshop")}
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2 p-3 bg-bg-soft border border-border rounded-lg" data-testid="status-filter-bar">
        <Filter className="w-4 h-4 text-text-secondary" />
        <span className="text-xs font-bold uppercase tracking-wider text-text-secondary mr-2">{isEn ? "Filter" : "Filtrer"}</span>
        <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="filter-all">
          {isEn ? "All" : "Tous"} · {ateliers.length}
        </FilterChip>
        <FilterChip active={statusFilter === "published"} onClick={() => setStatusFilter("published")} testId="filter-published" tone="success">
          {t("editor.published")} · {counts.published}
        </FilterChip>
        <FilterChip active={statusFilter === "draft"} onClick={() => setStatusFilter("draft")} testId="filter-draft" tone="warning">
          {t("editor.draft")} · {counts.draft}
        </FilterChip>
        <FilterChip active={statusFilter === "archived"} onClick={() => setStatusFilter("archived")} testId="filter-archived" tone="muted">
          {t("editor.archived")} · {counts.archived || 0}
        </FilterChip>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="p-8 text-center text-text-secondary text-sm" data-testid="empty-state">
            {isEn ? "No workshop in this filter." : "Aucun atelier dans ce filtre."}
          </div>
        )}
        {filtered.map((a) => {
          const isBusy = busyRow === a.id;
          return (
            <div
              key={a.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-white border border-border rounded-lg hover:border-apollo-orange/40 transition-colors"
              data-testid={`editor-row-${a.id}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="font-mono text-xs px-2 py-0.5 rounded bg-bg-soft border border-border shrink-0">{a.id}</span>
                <StatusBadge status={a.status} />
                <span className="font-medium text-sm truncate">{a.title_fr}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                {a.status !== "published" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => changeStatus(a, "published")}
                    data-testid={`btn-publish-${a.id}`}
                    title={isEn ? "Make workshop prescribable by AI" : "Rendre l'atelier prescriptible par l'IA"}
                  >
                    <Send className="w-3.5 h-3.5 mr-1" /> {t("editor.publish")}
                  </Button>
                )}
                {a.status === "published" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => changeStatus(a, "draft")}
                    data-testid={`btn-unpublish-${a.id}`}
                    title={isEn ? "Remove from active catalog for editing" : "Retirer du catalogue actif pour modification"}
                  >
                    <FileEdit className="w-3.5 h-3.5 mr-1" /> {t("editor.draft")}
                  </Button>
                )}
                {a.status !== "archived" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => changeStatus(a, "archived")}
                    data-testid={`btn-archive-${a.id}`}
                    title={isEn ? "Remove from catalog but keep history" : "Retirer du catalogue mais conserver l'historique"}
                  >
                    <Archive className="w-3.5 h-3.5 mr-1" /> {t("editor.archive")}
                  </Button>
                )}
                {a.status === "archived" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => changeStatus(a, "draft")}
                    data-testid={`btn-unarchive-${a.id}`}
                    title={isEn ? "Restore from archive to draft" : "Sortir des archives en brouillon"}
                  >
                    <FileEdit className="w-3.5 h-3.5 mr-1" /> {isEn ? "Unarchive" : "Désarchiver"}
                  </Button>
                )}
                <Button variant="ghost" size="sm" disabled={isBusy} onClick={() => startEdit(a)} data-testid={`btn-edit-${a.id}`}>
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> {isEn ? "Edit" : "Éditer"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => hardDelete(a)}
                  data-testid={`btn-delete-${a.id}`}
                  className="text-danger hover:bg-danger/10 hover:text-danger"
                  title={isEn ? "Permanent delete (refused if history exists)" : "Supprimer définitivement (refusé si historique existe)"}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const { t, i18n } = useTranslation();
  const isEn = (i18n.language || "fr").startsWith("en");
  const cfg = {
    published: { label: t("editor.published"), cls: "bg-success/10 text-success border border-success/20" },
    draft:     { label: t("editor.draft"), cls: "bg-warning/10 text-warning border border-warning/20" },
    archived:  { label: t("editor.archived"), cls: "bg-text-secondary/10 text-text-secondary border border-text-secondary/20" },
  }[status] || { label: status, cls: "bg-bg-soft text-text-secondary border border-border" };
  // unused but lets isEn satisfy hooks: kept for future fine-grained overrides
  void isEn;
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${cfg.cls}`}
      data-testid={`status-badge-${status}`}
    >
      {cfg.label}
    </span>
  );
}

function FilterChip({ active, onClick, testId, children, tone = "default" }) {
  const baseTone = {
    default: active ? "bg-apollo-orange text-white" : "bg-white border border-border text-text-secondary hover:border-apollo-orange/40",
    success: active ? "bg-success text-white" : "bg-white border border-border text-text-secondary hover:border-success/40",
    warning: active ? "bg-warning text-white" : "bg-white border border-border text-text-secondary hover:border-warning/40",
    muted:   active ? "bg-text-secondary text-white" : "bg-white border border-border text-text-secondary hover:border-text-secondary/40",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${baseTone}`}
    >
      {children}
    </button>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1.5 block">{label}{required && <span className="text-danger ml-0.5">*</span>}</span>
      {children}
    </label>
  );
}

function MultiSelect({ label, items, selected, onToggle, testIdPrefix }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
        {items.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => onToggle(item.value)}
            data-testid={`${testIdPrefix}-${item.value}`}
            className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
              selected.includes(item.value) ? "bg-apollo-orange text-white" : "bg-bg-soft border border-border text-text-secondary hover:border-apollo-orange/40"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
